import { type EventCallback, EventEmitter } from '../utils/event-emitter';
import { subscribe, unsubscribe } from './api';
import { SSEEvent, SSEEventMap, SSEMarketBody, SubEvents } from './type';
import type { EventSourceClient, EventSourceMessage, FetchLikeInit, ReadyState } from 'eventsource-client';
import { createEventSource } from 'eventsource-client';
import ky, { Options } from 'ky';

// Use function overloads instead of conditional types
export type SSEApi = {
  readyState: ReadyState | undefined;
  lastEventId: string | undefined;
  connect: () => Promise<string>;
  disconnect: () => void;
  isConnected: () => boolean;
  eventNames: () => SSEEvent[];
  hasListeners: (eventName: SSEEvent) => boolean;
  addEventListener<T extends SubEvents>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json: SSEMarketBody
  ): Promise<void>;
  addEventListener<T extends Exclude<SSEEvent, SubEvents>>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json?: SSEMarketBody
  ): Promise<void>;
  removeEventListener<T extends SubEvents>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json: SSEMarketBody
  ): Promise<void>;
  removeEventListener<T extends Exclude<SSEEvent, SubEvents>>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json?: SSEMarketBody
  ): Promise<void>;
};

export const isSubEvent = (eventName: SSEEvent): eventName is SubEvents => {
  return ['market:new_trades', 'market:price_change', 'market:info_update', 'market:price_tick'].includes(eventName);
};

// The usable topics of a body: non-empty strings. A value that is not an
// array gives no topic. The filter and the server get only these topics.
const usableTopics = (topics: unknown): string[] =>
  Array.isArray(topics) ? topics.filter((topic): topic is string => typeof topic === 'string' && topic !== '') : [];

// The lowercase market addresses that one market listener accepts. Undefined
// means no filter: a non-market event, or a body without a usable topic.
const toTopicFilter = (eventName: SSEEvent, json?: SSEMarketBody): Set<string> | undefined => {
  if (!isSubEvent(eventName)) return undefined;
  const topics = usableTopics(json?.topics);
  return topics.length > 0 ? new Set(topics.map(topic => topic.toLowerCase())) : undefined;
};

// Identifies the topics of a registration, so a removal finds the registration that it undoes.
const topicFilterKey = (filter: Set<string> | undefined) => (filter ? [...filter].sort().join(',') : '');

// The server routes by exact topic string and publishes the publisher's market
// address (checksum); callers pass the API address (checksum), so cases match
// in practice. The case-insensitive compare is defence only. A payload without
// an address is delivered (fail open).
const matchesTopicFilter = (filter: Set<string>, data: unknown) => {
  const address = (data as { market_address?: unknown } | null)?.market_address;
  return typeof address !== 'string' || address === '' || filter.has(address.toLowerCase());
};

// serverTopics: the server topic refs that the registration holds. Undefined
// when it holds none (a non-market event, or no body).
type Registration = { wrappedCallback: EventCallback<any>; topicKey: string; serverTopics?: string[] };

// A removal undoes the latest registration with the same topics. Without one,
// it undoes the latest registration, as before the topic filter.
const findRegistrationIndex = (registrations: Registration[], topicKey: string) => {
  for (let index = registrations.length - 1; index >= 0; index--) {
    if (registrations[index].topicKey === topicKey) return index;
  }
  return registrations.length - 1;
};

export const createSSEApi = (initOptions: Options): SSEApi => {
  const mergedOptions: Options = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/sse` };
  const sseApi = ky.extend(mergedOptions);
  const emitter = new EventEmitter<{ [K in SSEEvent]: [SSEEventMap[K]] }>();
  const callbackMap = new WeakMap<EventCallback<any>, Map<SSEEvent, Registration[]>>();
  const topicRefs = new Map<string, number>();
  const appliedTopics = new Set<string>();
  let sse: EventSourceClient | undefined;
  let connectionId: string | undefined;
  let appliedConnectionId: string | undefined;
  let connectPromise: Promise<string> | null = null;
  let reconcileQueue: Promise<void> = Promise.resolve();
  let reconcileRetryTimer: NodeJS.Timeout | undefined;
  let reconcileRetryAttempts = 0;
  // Reconnection state
  let isManualDisconnect = false;
  let reconnectAttempts = 0;
  let reconnectTimer: NodeJS.Timeout | undefined;
  const maxReconnectDelay = 30000;

  const isConnected = () => !!connectionId;
  const resetConnection = () => {
    connectionId = undefined;
    connectPromise = null;
  };
  const resetAppliedTopics = () => {
    appliedConnectionId = undefined;
    appliedTopics.clear();
  };
  const clearReconcileRetry = () => {
    clearTimeout(reconcileRetryTimer);
    reconcileRetryTimer = undefined;
    reconcileRetryAttempts = 0;
  };
  const disconnect = () => {
    isManualDisconnect = true;
    clearTimeout(reconnectTimer);
    sse?.close();
    sse = undefined;
    resetConnection();
    resetAppliedTopics();
    topicRefs.clear();
    clearReconcileRetry();
  };

  const updateTopicRefs = (topics: string[], delta: 1 | -1) => {
    for (const topic of new Set(usableTopics(topics))) {
      const nextRefCount = (topicRefs.get(topic) ?? 0) + delta;
      if (nextRefCount > 0) {
        topicRefs.set(topic, nextRefCount);
      } else {
        topicRefs.delete(topic);
      }
    }
    if (topicRefs.size === 0) clearReconcileRetry();
  };

  const reconcileTopics = async () => {
    if (topicRefs.size > 0 && !connectionId) {
      await connect();
    }

    const activeConnectionId = connectionId;
    if (!activeConnectionId) {
      resetAppliedTopics();
      return;
    }

    if (appliedConnectionId !== activeConnectionId) {
      resetAppliedTopics();
      appliedConnectionId = activeConnectionId;
    }

    let desiredTopics = new Set(topicRefs.keys());
    const topicsToUnsubscribe = [...appliedTopics].filter(topic => !desiredTopics.has(topic));
    if (topicsToUnsubscribe.length > 0) {
      await unsubscribe(sseApi, { connection_id: activeConnectionId, topics: topicsToUnsubscribe });
      if (connectionId !== activeConnectionId || appliedConnectionId !== activeConnectionId) return;
      for (const topic of topicsToUnsubscribe) appliedTopics.delete(topic);
    }

    desiredTopics = new Set(topicRefs.keys());
    const topicsToSubscribe = [...desiredTopics].filter(topic => !appliedTopics.has(topic));
    if (topicsToSubscribe.length > 0) {
      await subscribe(sseApi, { connection_id: activeConnectionId, topics: topicsToSubscribe });
      if (connectionId !== activeConnectionId || appliedConnectionId !== activeConnectionId) return;
      for (const topic of topicsToSubscribe) appliedTopics.add(topic);
    }
  };

  const enqueueReconcile = () => {
    const operation = reconcileQueue.then(reconcileTopics);
    reconcileQueue = operation.catch(() => undefined);
    return operation;
  };

  const scheduleReconcileRetry = () => {
    const maxRetryAttempts = 5;
    if (
      reconcileRetryTimer ||
      reconcileRetryAttempts >= maxRetryAttempts ||
      isManualDisconnect ||
      !connectionId ||
      topicRefs.size === 0
    )
      return;
    const delay = 1000 * Math.pow(2, reconcileRetryAttempts);
    reconcileRetryAttempts++;
    reconcileRetryTimer = setTimeout(() => {
      reconcileRetryTimer = undefined;
      enqueueReconcile()
        .then(clearReconcileRetry)
        .catch(error => {
          console.error('[SSE] Reconcile retry failed:', error);
          scheduleReconcileRetry();
        });
    }, delay);
  };

  const enqueueReconcileWithRetry = () => {
    const operation = enqueueReconcile();
    void operation.then(clearReconcileRetry, scheduleReconcileRetry);
    return operation;
  };

  const scheduleReconnect = () => {
    if (isManualDisconnect) return;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), maxReconnectDelay);
    console.log(`[SSE] Reconnecting in ${delay}ms... (Attempt ${reconnectAttempts + 1})`);
    reconnectTimer = setTimeout(() => {
      reconnectAttempts++;
      connect()
        .then(() => {
          enqueueReconcileWithRetry().catch(error => console.error('[SSE] Resubscribe failed:', error));
        })
        .catch(err => {
          console.error('[SSE] Reconnect failed:', err);
          scheduleReconnect();
        });
    }, delay);
  };

  const connect = async (): Promise<string> => {
    if (!connectPromise && !sse) {
      isManualDisconnect = false;
    }
    if (connectionId) return connectionId;
    if (connectPromise) return connectPromise;
    if (!sse) {
      sse = createEventSource({
        url: './connect',
        method: 'POST',
        onMessage(message: EventSourceMessage) {
          const { event, data } = message as EventSourceMessage & { event: SSEEvent };
          const result = JSON.parse(data) as SSEEventMap[SSEEvent];
          if (event === 'base:connected') {
            connectionId = (result as SSEEventMap['base:connected']).connection_id;
          }
          emitter.emit(event, result);
        },
        onDisconnect() {
          if (connectionId) {
            emitter.emit('base:disconnected', { connection_id: connectionId });
          }
          sse?.close();
          sse = undefined;
          resetConnection();
          resetAppliedTopics();
          clearReconcileRetry();
          if (!isManualDisconnect) {
            scheduleReconnect();
          }
        },
        fetch: async (url: string | URL, init?: FetchLikeInit) => {
          const options = { ...mergedOptions, ...init };
          return await sseApi(url, options);
        },
      });
    }

    connectPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        sse?.close();
        sse = undefined;
        resetConnection();
        resetAppliedTopics();
        reject(new Error('Connection timeout'));
      }, mergedOptions.timeout || 10e3);
      const handleConnected = (data: SSEEventMap['base:connected']) => {
        clearTimeout(timeout);
        connectionId = data.connection_id;
        emitter.off('base:connected', handleConnected);
        connectPromise = null;
        reconnectAttempts = 0;
        resolve(connectionId);
      };
      emitter.once('base:connected', handleConnected);
      sse?.connect();
    });
    return connectPromise;
  };

  return {
    connect,
    disconnect,
    readyState: sse?.readyState,
    lastEventId: sse?.lastEventId,
    isConnected,
    eventNames: () => emitter.eventNames(),
    hasListeners: (eventName: SSEEvent) => emitter.hasListeners(eventName),
    addEventListener: async (
      eventName: SSEEvent,
      callback: EventCallback<SSEEventMap[SSEEvent][]>,
      json?: SSEMarketBody
    ) => {
      // A market listener gets only the events of its own topics.
      const filter = toTopicFilter(eventName, json);
      const wrappedCallback = (data: SSEEventMap[SSEEvent]) => {
        if (filter && !matchesTopicFilter(filter, data)) return;
        callback(data);
      };
      emitter.on(eventName, wrappedCallback);
      const callbacksByEvent = callbackMap.get(callback) ?? new Map<SSEEvent, Registration[]>();
      const registrations = callbacksByEvent.get(eventName) ?? [];
      const serverTopics = isSubEvent(eventName) && json ? usableTopics(json.topics) : undefined;
      registrations.push({ wrappedCallback, topicKey: topicFilterKey(filter), serverTopics });
      callbacksByEvent.set(eventName, registrations);
      callbackMap.set(callback, callbacksByEvent);
      if (serverTopics) {
        updateTopicRefs(serverTopics, 1);
        await enqueueReconcileWithRetry();
      }
    },
    removeEventListener: async (
      eventName: SSEEvent,
      callback: EventCallback<SSEEventMap[SSEEvent][]>,
      json?: SSEMarketBody
    ) => {
      const callbacksByEvent = callbackMap.get(callback);
      const registrations = callbacksByEvent?.get(eventName) ?? [];
      const index = findRegistrationIndex(registrations, topicFilterKey(toTopicFilter(eventName, json)));
      const registration: Registration | undefined = index >= 0 ? registrations.splice(index, 1)[0] : undefined;
      const wrappedCallback = registration?.wrappedCallback;
      if (wrappedCallback) {
        emitter.off(eventName, wrappedCallback);
        if (registrations.length === 0) callbacksByEvent?.delete(eventName);
        if (callbacksByEvent?.size === 0) callbackMap.delete(callback);
      }
      // Release the refs of the removed registration, not the topics of this call.
      if (registration?.serverTopics) {
        updateTopicRefs(registration.serverTopics, -1);
        await enqueueReconcileWithRetry();
      }
    },
  };
};

export default createSSEApi;
